import { checkSession, updateUserPassword } from './api/auth.js';
import { supabaseClient } from './api/supabaseClient.js';
import { fetchWallets } from './api/wallets.js';
import { fetchExchangeRates } from './utils/format.js';
import { fetchTransactions } from './api/transactions.js';
import { appState } from './store/appState.js';

import { initAuthComponent } from './components/authComponent.js';
import { initNavigation } from './components/navigation.js';
import { renderWalletsTab } from './components/tabs/walletsTab.js';
import { renderTransactionsTab, initTransactionModals } from './components/tabs/transactionsTab.js';
import { renderDashboardTab } from './components/tabs/dashboardTab.js';
import { renderStatsTab } from './components/tabs/statsTab.js';
import { initTransactionModal } from './components/modals/transactionModal.js';
import { initAddWalletModal } from './components/modals/addWalletModal.js';
import { initWalletDetailModal } from './components/modals/walletDetailModal.js';
import { initItemPickerModal } from './components/modals/itemPickerModal.js';
import { initCurrencyPickerModal } from './components/modals/currencyPickerModal.js';
import { initSettingsModals } from './components/tabs/settingsTab.js';
import { initCategoryManager } from './components/tabs/categoryManager.js';
import { initBudgetManager } from './components/tabs/budgetManager.js';
import { initFilterModal } from './components/modals/filterModal.js';
import { initUiUtils } from './components/uiUtils.js';

import './style.css'; // Import global styles for Vite

// Khởi chạy App sau khi 로그인 success
async function initApp() {
    // 1. Tải dữ liệu ban đầu
    await fetchWallets();
    await fetchExchangeRates();
    
    // 2. Setup Modals
    initTransactionModal();
    initTransactionModals(); // Edit/Delete detail modal
    initAddWalletModal();
    initWalletDetailModal();
    initItemPickerModal();
    initCurrencyPickerModal();
    initSettingsModals();
    initCategoryManager();
    
    // 3. Setup global generic UI
    initUiUtils();
    initFilterModal();
    initBudgetManager();
    
    // 4. Expose hàm refresh toàn cục để các module khác gọi
    window.refreshAllActiveViews = async function() {
        const tabDash  = document.getElementById('tab-dash');
        const tabTxns  = document.getElementById('tab-txns');
        const tabWalt  = document.getElementById('tab-wallets');
        const tabStats = document.getElementById('tab-stats');

        const dashVisible  = tabDash  && !tabDash.classList.contains('hidden');
        const txnsVisible  = tabTxns  && !tabTxns.classList.contains('hidden');
        const waltVisible  = tabWalt  && !tabWalt.classList.contains('hidden');
        const statsVisible = tabStats && !tabStats.classList.contains('hidden');

        // Pre-fetch transactions 1 lần nếu có tab nào cần (dashboard/txns/stats)
        if (dashVisible || txnsVisible || statsVisible) {
            appState.currentTransactionsList = await fetchTransactions();
        }

        const promises = [];
        if (dashVisible)  promises.push(renderDashboardTab());
        if (txnsVisible)  promises.push(renderTransactionsTab(true));
        if (waltVisible)  promises.push(renderWalletsTab());
        if (statsVisible) promises.push(renderStatsTab());

        // Nếu không tab nào đang hiện → reload Dashboard mặc định
        if (promises.length === 0) {
            appState.currentTransactionsList = await fetchTransactions();
            await renderDashboardTab();
        }
    };

    // 5. Khởi chạy tab đầu tiên (Dashboard)
    renderDashboardTab();
}

document.addEventListener('DOMContentLoaded', async () => {
    const loginScreen = document.getElementById('login-screen');
    const mainApp = document.getElementById('main-app');

    // --- Supabase Password Recovery Flow ---
    // When user clicks reset-password link in email, Supabase redirects back
    // here with type=recovery in the URL hash. We intercept that event.
    supabaseClient.auth.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
            // Show the recovery modal OVER everything
            const recoveryModal = document.getElementById('recovery-password-modal');
            if (recoveryModal) {
                recoveryModal.classList.remove('hidden');
                recoveryModal.classList.add('flex');
            }
        }
    });

    // Wire up recovery submit button
    const btnSubmitRecovery = document.getElementById('btn-submit-recovery');
    if (btnSubmitRecovery) {
        btnSubmitRecovery.addEventListener('click', async () => {
            const newPwd = document.getElementById('input-recovery-pwd').value;
            const errEl = document.getElementById('recovery-error');
            errEl.classList.add('hidden');

            if (!newPwd || newPwd.length < 6) {
                errEl.innerText = 'Password must be at least 6 characters.';
                errEl.classList.remove('hidden');
                return;
            }

            btnSubmitRecovery.innerText = 'Saving...';
            btnSubmitRecovery.disabled = true;

            const res = await updateUserPassword(newPwd);
            
            btnSubmitRecovery.innerText = 'Save New Password';
            btnSubmitRecovery.disabled = false;

            if (res.success) {
                // Hide modal and let user log in with new password
                document.getElementById('recovery-password-modal').classList.add('hidden');
                document.getElementById('recovery-password-modal').classList.remove('flex');
                
                // Show success in login screen
                const loginError = document.getElementById('login-error');
                loginError.classList.remove('text-expense');
                loginError.classList.add('text-income');
                loginError.innerText = 'Password updated! Please log in with your new password.';
                loginError.classList.remove('hidden');
                
                // Clear any auto-logged-in session so user must re-login
                await supabaseClient.auth.signOut();
            } else {
                errEl.innerText = res.message;
                errEl.classList.remove('hidden');
            }
        });
    }
    // --- End Recovery Flow ---

    // Khởi tạo các event listener mặc định (độc lập auth)
    initNavigation();
    initAuthComponent(async () => {
        // Callback khi login form success
        await initApp();
    });

    // Sau khi gán event xong mới đi fetch Session để không block UI đầu tiên
    const isLoggedIn = await checkSession();
    
    if (isLoggedIn) {
        // Có token -> Tự động vào App, không cần gõ Pass
        loginScreen.classList.add('hidden');
        mainApp.classList.remove('hidden');
        mainApp.classList.add('flex');
        
        // Khởi tạo app
        await initApp();
    }

    // Global background click logic for all modals
    document.addEventListener('click', (e) => {
        // The modal overlay is the direct target when clicking outside the modal content window
        if (e.target && e.target.id && e.target.id.endsWith('-modal') && e.target.classList.contains('fixed')) {
            // Find generic close button
            const closeBtn = e.target.querySelector('button[id^="btn-close"]');
            if (closeBtn) {
                closeBtn.click();
            } else {
                // Find generic cancel button (for confirm modals)
                const cancelBtn = e.target.querySelector('button[id*="-cancel"]');
                if (cancelBtn) {
                    cancelBtn.click();
                }
            }
        }
    });
});
